import { stringIfExists } from '~utils/helpers'

export const AdminRoutes = {
    Users: '/admin/users',
    NewUser: '/admin/users/new',
    EditUser: (id: string) => `/admin/users/${id}`,
    Clinics: '/admin/clinics',
    NewClinic: '/admin/clinics/new',
    EditClinic: (id: string) => `/admin/clinics/${id}`,
    PmsDbs: '/admin/pms-dbs',
    PmsDb: (id: string) => `/admin/pms-dbs/${id}`,
    Organizations: '/admin/organizations',
    EditOrganization: (id: string) => `/admin/organizations/${id}`,
    Usage: '/admin/usage',
    UsageForClinic: (id: string) => `/admin/usage/${id}`,
    ClinicGroups: '/admin/clinic-groups',
    NewClinicGroup: '/admin/clinic-groups/new',
    EditClinicGroup: (id: string) => `/admin/clinic-groups/${id}`,
    EzyVetTools: '/admin/tools/ezyvet',
    InstinctTools: '/admin/tools/instinct',
    BitwerxTools: '/admin/tools/bitwerx',
    SupportBot: '/admin/tools/support-bot',
    ClinicOnboarding: (id: string) => `/admin/onboarding/${id}`,
    OnboardingWizard: (id: string) => `/admin/onboarding/wizard/${id}`,
}

// Broken out by sidebar
export const UserRoutes = {
    // Home
    Home: '/',

    // Support
    Support: '/support',

    // Daily Huddle
    DailyHuddle: '/daily-huddle',

    // Pending Actions
    PendingActions: '/pending-actions',
    PendingActionsHistory: '/pending-actions/history',

    // Team Chat
    TeamChat: '/team-chat',
    TeamChatWithId: (id: string) => `/team-chat/${id}`,

    // Messages
    Chats: '/chats',
    ChatConversation: (id: string) => `/chats?chatId=${id}`,
    Whiteboard: '/whiteboard',

    // Analytics\
    DataChat: '/analytics/data-chat',
    DataChatWithId: (id: string) => `/analytics/data-chat?dataChat=${id}`,
    ClinicReporting: '/analytics/reporting',
    ClinicTrends: '/analytics/trends',
    ClinicInitiatives: '/analytics/initiatives',
    ClinicKpiDashboard: '/analytics/kpis',
    ClinicMetrics: '/analytics/metrics',
    ClinicBookingSources: '/analytics/booking-sources',
    ClinicNotificationAnalytics: '/analytics/notification-analytics',
    NewClinicMetric: '/analytics/metrics/new',
    EditClinicMetric: (id: string) => `/analytics/metrics/${id}`,
    ViewClinicReport: (id: string) => `/analytics/reporting/${id}`,
    NewClinicTemplate: '/analytics/reporting/new',
    EditClinicTemplate: (id: string) => `/analytics/reporting/${id}/edit`,
    Goals: '/analytics/goals',
    NewGoal: '/analytics/goals/new',
    EditGoal: (id: string) => `/analytics/goals/${id}`,

    // Booking
    SchedulingRules: '/booking/scheduling-rules',
    NewSchedulingRule: '/booking/scheduling-rules/new',
    NewWidgetSchedulingRule: (id: string) =>
        `/booking/scheduling-rules/new/${id}`,
    EditSchedulingRule: (id: string) => `/booking/scheduling-rules/${id}`,
    Widgets: '/booking/widgets',
    NewWidget: '/booking/widgets/new',
    EditWidget: (id: string) => `/booking/widgets/${id}`,
    WidgetInstructions: (id: string) => `/booking/widgets/${id}/instructions`,
    WidgetPreview: (id: string, type: string) =>
        `/booking/widgets/${id}/preview/${type}`,
    WidgetReports: (id: string) => `/booking/widgets/reports/${id}`,
    Clinicians: '/booking/clinicians',
    Rooms: '/booking/rooms',
    Payments: '/booking/payments',
    Services: '/booking/services',
    NewService: '/booking/services/new',
    EditService: (id: string) => `/booking/services/${id}`,
    NewServiceCategory: '/booking/services/categories/new',
    EditServiceCategory: (id: string) => `/booking/services/categories/${id}`,
    AvailabilityChecker: '/booking/availability-checker',
    PrescriptionRefillRequests: '/booking/prescription-refill-requests',

    // Engagement
    Campaigns: '/engagement/campaigns',
    ViewCampaign: (id: string) => `/engagement/campaigns/${id}`,
    Audiences: '/engagement/audiences',
    AudienceDetails: (id: string) => `/engagement/audiences/${id}`,
    EditAudience: (id: string) => `/engagement/audiences/edit/${id}`,
    NewAudience: '/engagement/audiences/new',
    Forms: '/engagement/forms',
    FormAnalytics: (formName?: string) =>
        formName
            ? `/engagement/forms/analytics?formName=${encodeURIComponent(formName)}`
            : '/engagement/forms/analytics',
    EditForm: (id: string) => `/engagement/forms/edit/${id}`,
    FormResponses: (id?: string) =>
        id
            ? `/engagement/forms/responses?formId=${id}`
            : '/engagement/forms/responses',
    ViewFormResponse: (id: string) => `/engagement/forms/responses/${id}`,
    Notifications: '/engagement/notifications',
    EditNotification: (id: string) => `/engagement/notifications/edit/${id}`,
    NotificationReports: (id: string) =>
        `/engagement/notifications/reports/${id}`,
    NotificationLogs: '/engagement/notification-logs',
    MassCommunication: '/engagement/mass-communication',
    EditScheduledBulkMessage: (id: string) =>
        `/engagement/mass-communication/edit/${id}`,
    MassCommunicationReports: (id: string) =>
        `/engagement/mass-communication/reports/${id}`,
    Loyalty: '/loyalty',
    ComplianceTracker: '/engagement/compliance-tracker',
    Reviews: '/engagement/reviews',
    MessageTemplates: '/engagement/message-templates',
    EmailTemplates: '/engagement/email-templates',
    EditEmailTemplate: (id: string) => `/engagement/email-templates/${id}`,
    SharedMedia: '/engagement/shared-media',

    // Clinic
    Appointments: '/clinic/appointments',
    AppointmentDetails: (id: string) => `/clinic/appointments/${id}`,
    ClientsPatients: '/clinic/clients',
    ClientDetails: (id: string) => `/clinic/clients/${id}`,
    PatientDetails: (id: string) => `/clinic/patients/${id}`,
    ClinicInfo: '/clinic/info',
    ClinicUsers: '/clinic/users',
    ManageClinicUser: (id: string) => `/clinic/users/${id}`,
    CustomRoles: '/clinic/users/custom-roles',
    Calls: '/clinic/calls',
    DataSync: '/clinic/data-sync',

    // Patient Portal
    PortalCustomization: '/patient-portal/customization',
    MarketingMaterials: '/patient-portal/marketing-materials',
    PetSelfies: '/patient-portal/pet-selfies',
    UserTracking: '/patient-portal/user-tracking',

    // Settings
    Settings: '/settings',
    GeneralSettings: '/settings/general',
    BillingSettings: '/settings/billing',
    AnalyticsSettings: '/settings/analytics',
    BookingSettings: '/settings/booking',
    EngagementSettings: '/settings/engagement',
    SyncSettings: '/settings/sync',

    // Help
    Help: '/help',
    HelpSearch: '/help/search',

    // Auth/Misc
    404: '/404',
    Auth: '/auth',
    AuthWithRedirect: (redirect: string) => `/auth?redirect=${redirect}`,
    AuthWithRegister: '/auth?register=true',
    MFARegistration: '/auth/register-mfa',
    ForgotPassword: '/auth/forgot-password',
    ForceResetPassword: '/auth/force-password-reset',
    SelectClinic: '/select-clinic',
    IntegrationUpload: '/integration/upload',
    Profile: '/profile',
    RhapsodyWidget: '/rhapsody-widget',

    // Enterprise
    Reports: '/enterprise/reports',
    KpiDashboard: '/enterprise/kpis',
    OrganizationBranding: '/enterprise/branding',
    ReportsList: '/enterprise/reports/list',
    NewReportTemplate: '/enterprise/reports/new',
    ReportTemplate: (id: string) => `/enterprise/reports/${id}/edit`,
    ReportTemplateView: (id: string) => `/enterprise/reports/${id}`,
    ClinicGroupUsers: '/enterprise/users',
    ManageClinicGroupUser: (id: string) => `/enterprise/users/${id}`,
    /** @deprecated Prefer EnterpriseStandardizationCustomRoles */
    ClinicGroupCustomRoles: '/enterprise/standardizations/custom-roles',
    EnterpriseStandardizationAudiences:
        '/enterprise/standardizations/audiences',
    EnterpriseStandardizationAudienceDetails: (id: string) =>
        `/enterprise/standardizations/audiences/${id}`,
    EnterpriseStandardizationBulkMessages:
        '/enterprise/standardizations/bulk-messages',
    EnterpriseStandardizationCoverage: '/enterprise/standardizations/coverage',
    EnterpriseStandardizationCustomRoles:
        '/enterprise/standardizations/custom-roles',
    EnterpriseStandardizationEmailTemplates:
        '/enterprise/standardizations/email-templates',
    EnterpriseStandardizationForms: '/enterprise/standardizations/forms',
    EnterpriseStandardizationMergeTags:
        '/enterprise/standardizations/merge-tags',
    EnterpriseStandardizationMessageTemplates:
        '/enterprise/standardizations/message-templates',
    EnterpriseStandardizationNotifications:
        '/enterprise/standardizations/notifications',
    EnterpriseSettings: '/enterprise/standardizations/settings',
    EnterpriseBookingSettings: '/enterprise/standardizations/settings/booking',
    EnterpriseEngagementSettings:
        '/enterprise/standardizations/settings/engagement',
    EnterpriseGroupInfo: '/enterprise/info',
    RealTimeReport: (id: string) => `/enterprise/reports/realtime/${id}`,
    ClinicTrendsQuery: (metricId?: string) =>
        metricId ? `/analytics/trends?metric=${metricId}` : `/analytics/trends`,
    TrendsDashboard: '/enterprise/trends',
    Trends: (metricId?: string) =>
        metricId
            ? `/enterprise/trends?metric=${metricId}`
            : `/enterprise/trends`,
    Initiatives: '/enterprise/initiatives',
    Metrics: '/enterprise/metrics',
    EditMetric: (id: string) => `/enterprise/metrics/${id}`,
    OrganizationWidgets: '/enterprise/widgets',
    NewOrganizationWidget: '/enterprise/widgets/new',
    EditOrganizationWidget: (id: string) => `/enterprise/widgets/${id}`,
    EnterpriseAppointments: '/enterprise/appointments',
    EnterpriseBookingSources: '/enterprise/booking-sources',
    EnterpriseNotificationAnalytics: '/enterprise/notification-analytics',
    NewClinicGroup: '/enterprise/clinic-groups/new',
    EnterpriseMarketing: '/enterprise/marketing',
    EnterpriseMarketingOverview: '/enterprise/marketing?tab=overview',
    EnterpriseMarketingChannels: '/enterprise/marketing?tab=channels',
    EnterpriseMarketingAppointments: '/enterprise/marketing?tab=appointments',
}
